import { useCallback, useState } from "react"

import type { TransferPalletWithProduct } from "../types"
import { toTransferPalletLabelParams } from "../utils"

import { useZplPrinting } from "@/hooks/useZplPrinting"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { generatePalletLabelZpl } from "@/utils/printing/generatePalletLabelZpl"

interface UseTransferPalletLabelPrintingReturn {
  printPalletLabels: (pallets: TransferPalletWithProduct[]) => Promise<boolean>
  isPrinting: boolean
}

export const useTransferPalletLabelPrinting = (): UseTransferPalletLabelPrintingReturn => {
  const [isPrinting, setIsPrinting] = useState(false)

  const { printZpl } = useZplPrinting()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const printPalletLabels = useCallback(
    async (pallets: TransferPalletWithProduct[]): Promise<boolean> => {
      if (!pallets.length) {
        notify({
          type: NotificationType.Info,
          message: "No pallets found for printing",
        })
        return false
      }

      setIsPrinting(true)

      try {
        const sortedPallets = [...pallets].sort((a, b) => a.referenceNbr - b.referenceNbr)

        for (const pallet of sortedPallets) {
          const success = await printZpl(generatePalletLabelZpl(toTransferPalletLabelParams(pallet)))

          if (!success) return false
        }

        return true
      } finally {
        setIsPrinting(false)
      }
    },
    [notify, printZpl]
  )

  return {
    printPalletLabels,
    isPrinting,
  }
}
